/**
 * Enum value sets shared by the API and the web app.
 *
 * SQLite has no native enum type, so these are the authoritative lists and the
 * Drizzle schema declares the matching columns with `text({ enum: ... })`.
 * Values are ported verbatim from the Postgres scaffold on gigabyte
 * (`nexus_dev`) so a later migration back to Postgres is a straight mapping.
 */

export const USER_ROLES = ['OPERATOR', 'CREATOR'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PLATFORMS = ['TIKTOK', 'INSTAGRAM', 'YOUTUBE'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const BRAND_STATUSES = ['ACTIVE', 'ARCHIVED'] as const;
export type BrandStatus = (typeof BRAND_STATUSES)[number];

export const CAMPAIGN_STATUSES = [
  'DRAFT',
  'FUNDED',
  'ACTIVE',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CREATOR_STATUSES = [
  'INVITED',
  'PENDING_REVIEW',
  'APPROVED',
  'SUSPENDED',
  'REJECTED',
] as const;
export type CreatorStatus = (typeof CREATOR_STATUSES)[number];

export const SOCIAL_VERIFICATION_STATUSES = [
  'PENDING',
  'CHALLENGE_ISSUED',
  'VERIFIED',
  'REJECTED',
  'REVOKED',
] as const;
export type SocialVerificationStatus = (typeof SOCIAL_VERIFICATION_STATUSES)[number];

/** How ownership of a social account was proven. */
export const OWNERSHIP_METHODS = ['OAUTH', 'BIO_CODE', 'MANUAL'] as const;
export type OwnershipMethod = (typeof OWNERSHIP_METHODS)[number];

export const SUBMISSION_STATUSES = [
  'PENDING_CHECKS',
  'IN_REVIEW',
  'VERIFIED',
  'REJECTED',
  'HELD',
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

/**
 * Where a `SocialAccount.platformAccountId` came from, because the two sources
 * are NOT the same namespace on every platform.
 *
 * `STATS_PROVIDER` is the id `@weclip/stats-providers` reports for the account
 * (TikTok `uid`, Instagram `pk`, YouTube `channelId`) — the id phase 3 can
 * compare a post's `authorId` against, because a post read comes from the same
 * package.
 *
 * `OAUTH` is the id the provider's own identity endpoint returned. On YouTube
 * and Instagram that is the same value the stats provider reports; **on TikTok
 * it is not**: Login Kit returns an app-scoped `open_id`, which no post read
 * will ever echo back. That is why the source is recorded rather than assumed,
 * and why an OAuth connection also stores `statsAuthorId` whenever the public
 * profile can be read.
 */
export const PLATFORM_ID_SOURCES = ['OAUTH', 'STATS_PROVIDER'] as const;
export type PlatformIdSource = (typeof PLATFORM_ID_SOURCES)[number];

export const VERIFICATION_CHECK_TYPES = [
  /* --- account ownership (phase 2, keyed on socialAccountId) --- */
  /** The creator's bio contained the code Nexus issued. */
  'BIO_CODE',
  /** A read-only OAuth grant returned an identity for this account. */
  'OAUTH_IDENTITY',
  /** A later re-read of the public profile: followers, handle, the code again. */
  'ACCOUNT_REVERIFY',

  /* --- submissions (phase 3, keyed on submissionId) --- */
  'URL_REACHABLE',
  'PUBLIC_VISIBILITY',
  'PLATFORM_MATCH',
  'ACCOUNT_MATCH',
  'PUBLICATION_WINDOW',
  'DUPLICATE_CHECK',
  'CONTENT_REVIEW',
  /* Campaign content rules: required credit/hashtags and the #ad disclosure.
     Ported back from the scaffold — these are campaign rules, not asset-library
     checks, and `Campaign.contentRules` / `CampaignTermsVersion.disclosureRulesJson`
     are exactly what they decide against. */
  'CAPTION_REQUIREMENTS',
  'DISCLOSURE_REQUIREMENTS',
  'CURVE_SHAPE',
  'ENGAGEMENT_RATIO',
  'VELOCITY_SPIKE',
  'REPOST_ACROSS_ACCOUNTS',
  'NEW_ACCOUNT_CONCENTRATION',
  'SINGLE_POST_THRESHOLD',
] as const;
export type VerificationCheckType = (typeof VERIFICATION_CHECK_TYPES)[number];

export const VERIFICATION_CHECK_RESULTS = ['PASS', 'FAIL', 'EXCEPTION'] as const;
export type VerificationCheckResult = (typeof VERIFICATION_CHECK_RESULTS)[number];

export const VERIFICATION_METHODS = ['MANUAL', 'SYSTEM'] as const;
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number];

export const METRIC_SOURCES = [
  'STATS_PROVIDER',
  'MANUAL_OPERATOR',
  'CREATOR_EVIDENCE',
  'PLATFORM_OAUTH',
] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];

export const METRIC_CAPTURE_STATUSES = ['SUCCESS', 'PARTIAL', 'FAILURE'] as const;
export type MetricCaptureStatus = (typeof METRIC_CAPTURE_STATUSES)[number];

export const EARNING_TYPES = [
  'CPM_PERFORMANCE',
  'MANUAL_ADJUSTMENT',
  'REVERSAL',
] as const;
export type EarningType = (typeof EARNING_TYPES)[number];

export const EARNING_STATUSES = ['APPROVED', 'REVERSED', 'PAID'] as const;
export type EarningStatus = (typeof EARNING_STATUSES)[number];

export const LEDGER_ACCOUNT_TYPES = [
  'CAMPAIGN_FUNDS_AVAILABLE',
  'CAMPAIGN_FUNDS_RESERVED',
  'EXTERNAL_BRAND_FUNDS',
  'CREATOR_PAYABLE',
  'CREATOR_PAYOUTS_IN_TRANSIT',
  'EXTERNAL_CREATOR_PAYOUTS',
] as const;
export type LedgerAccountType = (typeof LEDGER_ACCOUNT_TYPES)[number];

export const LEDGER_OWNER_TYPES = ['CAMPAIGN', 'CREATOR', 'SYSTEM'] as const;
export type LedgerOwnerType = (typeof LEDGER_OWNER_TYPES)[number];

export const LEDGER_TRANSACTION_TYPES = [
  'FUNDING_RECORDED',
  'RESERVATION',
  'RESERVATION_RELEASE',
  'EARNING_RECORDED',
  'PAYOUT_RECORDED',
  'PAYOUT_DISPATCHED',
  'PAYOUT_FAILED_RETURN',
  'PAYOUT_REVERSED',
  'REVERSAL',
] as const;
export type LedgerTransactionType = (typeof LEDGER_TRANSACTION_TYPES)[number];

export const PAYOUT_PROVIDERS = ['MANUAL', 'STRIPE_CONNECT', 'PAYPAL'] as const;
export type PayoutProvider = (typeof PAYOUT_PROVIDERS)[number];

export const PAYOUT_PROFILE_STATUSES = ['PENDING', 'ELIGIBLE', 'SUSPENDED'] as const;
export type PayoutProfileStatus = (typeof PAYOUT_PROFILE_STATUSES)[number];

export const PAYOUT_STATUSES = [
  'DRAFT',
  'APPROVED',
  'PROCESSING',
  'PAID',
  'FAILED',
  'REVERSED',
  'CANCELLED',
] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const PAYOUT_ATTEMPT_STATUSES = [
  'SUBMITTED',
  'UNCERTAIN',
  'SUCCEEDED',
  'FAILED',
] as const;
export type PayoutAttemptStatus = (typeof PAYOUT_ATTEMPT_STATUSES)[number];

export const OUTBOUND_EMAIL_STATUSES = [
  'QUEUED',
  'SENDING',
  'SENT',
  'FAILED',
  'CANCELLED',
] as const;
export type OutboundEmailStatus = (typeof OUTBOUND_EMAIL_STATUSES)[number];

export const AUDIT_ACTOR_TYPES = ['OPERATOR', 'CREATOR', 'SYSTEM'] as const;
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

/**
 * A creator's standing on one campaign. `JOINED` is the normal state once the
 * campaign's terms have been accepted; `WITHDRAWN` is the creator leaving;
 * `REMOVED` is the operator removing them (a fraud hold, say) without deleting
 * the row that records what they accepted and when.
 */
export const CAMPAIGN_PARTICIPATION_STATUSES = ['JOINED', 'WITHDRAWN', 'REMOVED'] as const;
export type CampaignParticipationStatus = (typeof CAMPAIGN_PARTICIPATION_STATUSES)[number];

/** Ledger accounts are opened and, rarely, closed. Never deleted. */
export const LEDGER_ACCOUNT_STATUSES = ['ACTIVE', 'CLOSED'] as const;
export type LedgerAccountStatus = (typeof LEDGER_ACCOUNT_STATUSES)[number];
